import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// sessionStorage key — holds only an OPAQUE reference to the
// backend-issued Jericho token. The token itself authorises NOTHING
// client-side; every /jericho render re-validates with the server.
export const JERICHO_TOKEN_KEY = "archive_jericho_token";
// Legacy unlock flag — retained so old clients fail closed (the new
// guard will ignore it).
export const JERICHO_UNLOCK_KEY = "archive_jericho_unlocked";

/**
 * JerichoEasterEgg — hidden restricted-access terminal.
 *
 * Two ways to open the terminal:
 *  1) Triple-click the Archive logo within 2s (handler exposed via context)
 *  2) Type "JERICHO" while focused on the homepage <body>
 *
 * Successful passphrase ("JERICHO", case-insensitive) routes to /jericho.
 * Strictly frontend cosmetic — never touches admin auth.
 */

const SECRET = "JERICHO";
const TRIPLE_WINDOW_MS = 2000;
const KEY_BUFFER_RESET_MS = 1500;
// (JERICHO_UNLOCK_KEY / JERICHO_TOKEN_KEY exported above.)

const JerichoCtx = createContext({ registerLogoClick: () => {}, open: () => {} });
export const useJericho = () => useContext(JerichoCtx);

export const JerichoProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const clickTimes = useRef([]);
  const keyBuffer = useRef("");
  const keyBufferLastAt = useRef(0);
  const location = useLocation();

  const openTerminal = useCallback(() => setOpen(true), []);

  // Logo triple-click handler — wired from Header
  const registerLogoClick = useCallback(() => {
    const now = Date.now();
    clickTimes.current = clickTimes.current.filter((t) => now - t < TRIPLE_WINDOW_MS);
    clickTimes.current.push(now);
    if (clickTimes.current.length >= 3) {
      clickTimes.current = [];
      setOpen(true);
    }
  }, []);

  // Homepage-only keyboard sequence
  useEffect(() => {
    if (location.pathname !== "/") return undefined;
    const isInput = (el) => {
      if (!el) return false;
      const t = (el.tagName || "").toLowerCase();
      if (t === "input" || t === "textarea" || t === "select") return true;
      if (el.isContentEditable) return true;
      return false;
    };
    const onKey = (e) => {
      if (isInput(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const ch = (e.key || "").toUpperCase();
      if (!/^[A-Z]$/.test(ch)) return;
      const now = Date.now();
      if (now - keyBufferLastAt.current > KEY_BUFFER_RESET_MS) keyBuffer.current = "";
      keyBufferLastAt.current = now;
      keyBuffer.current = (keyBuffer.current + ch).slice(-SECRET.length);
      if (keyBuffer.current === SECRET) {
        keyBuffer.current = "";
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [location.pathname]);

  return (
    <JerichoCtx.Provider value={{ registerLogoClick, open: openTerminal }}>
      {children}
      <JerichoTerminal isOpen={open} onClose={() => setOpen(false)} />
    </JerichoCtx.Provider>
  );
};

// =============================================================================
// Terminal overlay
// =============================================================================
const JerichoTerminal = ({ isOpen, onClose }) => {
  const [phrase, setPhrase] = useState("");
  const [state, setState] = useState("idle"); // idle | denied | verifying | granted | unauthenticated
  const [bootLines, setBootLines] = useState([]);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { token: bearerToken, login } = useAuth();

  // Boot/typing intro
  useEffect(() => {
    if (!isOpen) {
      setBootLines([]); setPhrase(""); setState("idle");
      return undefined;
    }
    const lines = [
      "> initializing secure terminal",
      "> establishing internal channel",
      "> archive node handshake … OK",
      "> awaiting clearance phrase",
    ];
    let i = 0;
    setBootLines([]);
    const id = setInterval(() => {
      i += 1;
      setBootLines(lines.slice(0, i));
      if (i >= lines.length) {
        clearInterval(id);
        setTimeout(() => inputRef.current?.focus(), 150);
      }
    }, 230);
    return () => clearInterval(id);
  }, [isOpen]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (state === "granted" || state === "verifying") return;
    if (!bearerToken) {
      setState("unauthenticated");
      return;
    }
    setState("verifying");
    try {
      const res = await fetch(`${API}/jericho/unlock`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
        body: JSON.stringify({ passphrase: (phrase || "").trim() }),
      });
      if (!res.ok) {
        setState("denied");
        setTimeout(() => setState("idle"), 1800);
        return;
      }
      const data = await res.json();
      try { sessionStorage.setItem(JERICHO_TOKEN_KEY, data.token); }
      catch (_) { /* private mode — proceed */ }
      setState("granted");
      setTimeout(() => {
        onClose();
        navigate("/jericho");
      }, 1100);
    } catch (_) {
      setState("denied");
      setTimeout(() => setState("idle"), 1800);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          data-testid="jericho-terminal"
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center px-4 py-6"
        >
          {/* CRT scanline overlay */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none opacity-[0.08]"
            style={{
              backgroundImage:
                "repeating-linear-gradient(to bottom, rgba(255,255,255,0.5) 0px, rgba(255,255,255,0.5) 1px, transparent 1px, transparent 3px)",
            }}
          />
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{ background: "radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.6) 100%)" }}
          />

          <motion.div
            initial={{ y: 14, scale: 0.98 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: 14, scale: 0.98 }}
            transition={{ duration: 0.25 }}
            className="relative w-full max-w-xl border border-[#D92D20]/50 bg-[#0a0907] shadow-[0_0_0_1px_rgba(217,45,32,0.15),0_30px_60px_rgba(0,0,0,0.75),0_0_120px_rgba(217,45,32,0.10)]"
          >
            {/* Header strip */}
            <div className="flex items-center justify-between border-b border-[#2D2A26] px-3 py-2">
              <div className="flex items-center gap-2">
                <span className="block w-2 h-2 rounded-full bg-[#D92D20] animate-pulse" />
                <span className="font-mono text-[9px] uppercase tracking-[0.35em] text-[#D92D20]">
                  // SECURE LINE
                </span>
              </div>
              <button
                onClick={onClose}
                data-testid="jericho-close"
                className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93] hover:text-[#E4DFD3]"
                aria-label="Close"
              >
                close ✕
              </button>
            </div>

            <div className="p-5 sm:p-7 font-mono text-[#E4DFD3]">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#D92D20] mb-1">
                THE ARCHIVE
              </div>
              <h2 className="font-display text-2xl sm:text-3xl text-[#E4DFD3] leading-tight mb-2">
                RESTRICTED ACCESS TERMINAL
              </h2>
              <div className="text-[10px] uppercase tracking-[0.35em] text-[#A39E93] mb-4">
                ARCHIVE INTERNAL NODE · CLEARANCE REQUIRED
              </div>

              {/* Boot lines */}
              <div className="text-[11px] leading-relaxed text-[#7a8294] mb-4 min-h-[88px]">
                {bootLines.map((l, i) => (
                  <div key={i}>{l}</div>
                ))}
                {bootLines.length === 4 && (
                  <div className="text-[#D92D20]">
                    <span className="animate-pulse">▌</span>
                  </div>
                )}
              </div>

              <form onSubmit={submit}>
                <label className="block text-[10px] uppercase tracking-[0.35em] text-[#D92D20] mb-1.5">
                  // ENTER ACCESS PHRASE
                </label>
                <div className="relative">
                  <input
                    ref={inputRef}
                    value={phrase}
                    onChange={(e) => setPhrase(e.target.value)}
                    disabled={state === "granted"}
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    data-testid="jericho-input"
                    className="w-full bg-black border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-3 font-mono text-[14px] tracking-[0.3em] text-[#D92D20] placeholder:text-[#3a352e] uppercase"
                    placeholder="—"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#D92D20] animate-pulse pointer-events-none select-none">
                    ▌
                  </span>
                </div>

                {state === "denied" && (
                  <div
                    data-testid="jericho-denied"
                    className="mt-3 text-[10px] uppercase tracking-[0.3em] text-[#D92D20] border-l-2 border-[#D92D20] pl-2"
                  >
                    ACCESS DENIED // CLEARANCE NOT GRANTED
                  </div>
                )}
                {state === "unauthenticated" && (
                  <div
                    data-testid="jericho-unauthenticated"
                    className="mt-3 text-[10px] uppercase tracking-[0.3em] text-[#E0A526] border-l-2 border-[#E0A526] pl-2"
                  >
                    <div>// AUTHENTICATION REQUIRED</div>
                    <button
                      type="button"
                      onClick={() => { onClose(); login("/"); }}
                      className="mt-2 underline hover:text-[#F4C440]"
                      data-testid="jericho-sign-in"
                    >
                      sign in to continue
                    </button>
                  </div>
                )}
                {state === "verifying" && (
                  <div
                    data-testid="jericho-verifying"
                    className="mt-3 text-[10px] uppercase tracking-[0.3em] text-[#7a8294] border-l-2 border-[#454038] pl-2"
                  >
                    // VERIFYING WITH ARCHIVE NODE…
                  </div>
                )}
                {state === "granted" && (
                  <div
                    data-testid="jericho-granted"
                    className="mt-3 text-[10px] uppercase tracking-[0.3em] text-[#5DD4B3]"
                  >
                    <div>ACCESS PHRASE ACCEPTED</div>
                    <div>CLEARANCE VERIFIED</div>
                    <div className="text-[#E0A526]">OPENING NODE JERICHO…</div>
                  </div>
                )}

                <div className="mt-5 flex gap-2">
                  <button
                    type="submit"
                    disabled={state === "granted"}
                    data-testid="jericho-unlock"
                    className={
                      "flex-1 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.35em] border " +
                      (state === "granted"
                        ? "bg-[#0F2A20] border-[#5DD4B3]/70 text-[#5DD4B3] cursor-default"
                        : "bg-[#D92D20] hover:bg-[#B92318] border-[#D92D20] text-white")
                    }
                  >
                    UNLOCK
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    data-testid="jericho-cancel"
                    className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.35em] border border-[#454038] text-[#A39E93] hover:text-[#E4DFD3] hover:border-[#D92D20]"
                  >
                    CLOSE
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default JerichoProvider;
